import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "wouter";

import { BoxContentsProvider, PackingGroupDTO, TransportType } from "@boxem/core-api-schema";

import { deleteBoxV2, handleCreateBox, patchShipmentV2, updateBoxV2 } from "@/api/fulfillment";
import {
  FbaBatch,
  FbaBatchItem,
  FbaBox,
  BoxTemplate,
  FbaShipment,
  getItemAssignedQuantity,
  useShipmentContext,
  FbaShipmentPatch,
  getPackingGroupItem,
  getFbaBatchItemById
} from "@/contexts/ShipmentContext";
import PageContent from "@/components/PageContent";
import InfoBanner from "@/components/InformationBanner/InfoBanner";
import Button from "@/components/forms/Button/Button";
import IconShipment from "@/components/icons/IconShipment";
import IconBoxIso from "@/components/icons/IconBoxIso";
import IconCopyClipboard from "@/components/icons/IconCopyClipboard";
import IconPrintGoogle from "@/components/icons/IconPrintGoogle";
import Tabs from "@/components/Tabs/Tabs";
import { FbaPageProps } from "../FbaShipmentFlow";
import PageHeader from "../PageHeader";
import { FreightClasses, getDefaultDimensionsAndWeight } from "../../utils";
import FbaUpdateBoxDimensionsModal from "../../FbaUpdateBoxDimensionsModal/FbaUpdateBoxDimensionsModal";
import BoxDetailsModal from "../../BoxDetailsModal/BoxDetailsModal";
import { Barcode2DItem, fbaBoxLabel2DBarcode } from "@/contexts/pdf/box-label-generation";
import { logRocketError } from "@/utils/logUtils";
import { PDF417Error } from "@/contexts/printing/barcodes/pdf417";
import ConfirmModal from "@/components/ConfirmModal/ConfirmModal";
import BulkAssignModal from "./BulkAssignModal";
import AssignAllSkusModal from "./AssignAllSkusModal";
import PackingGroupView, { Item } from "./PackingGroup";
import { DimensionsAndWeight } from "../../utils";

import styles from "./PrepAndBoxemPage.module.css";
import { LabelsModal, LabelsModalItem } from "@/components/LabelsModal/LabelsModal";
import Select from "@/components/forms/Select";
import IconCautionCircle from "@/components/icons/IconCautionCircle";
import IconCheckCircle from "@/components/icons/IconCheckCircle";
import { Tooltip } from "@/components/Tooltip";
import ShipperInformationModal, { ShipperInformation } from "./ShipperInformationModal";
import dayjs from "dayjs";
import PalletInformationModal, { Pallet } from "./PalletInformationModal";
import { getLocalStorageItem, LocalStorageKeys, setLocalStorageItem } from "@/utils/localStorageUtils";
import { filterValues, mapValues } from "@/utils";

export type BoxState = { selected: boolean, open: boolean, id: number, pgId: string };
export type BoxesState = { [key: number]: BoxState };

export type BoxUpdate = {
  boxId?: number;
  newItem?: boolean;
  removedItem?: boolean;
  item?: FbaBatchItem;
  quantity?: number;
  dimensionsAndWeight?: DimensionsAndWeight;
  packingGroupId?: string;
  noop?: boolean;
};

export type AssignDialogProps = {
  packingGroupId?: string;
  items?: Item[];
  index?: number;
  open?: boolean;
};

function selectedPackingGroupFromQuery(packingGroups: PackingGroupDTO[], queryGroup: string|null): PackingGroupDTO {
  return packingGroups.find(pg => pg.inboundPackingGroupId === queryGroup) ?? packingGroups[0];
}

function focusedShipmentFromQuery(batch: FbaBatch, queryShipment: string|null) {
  const n = parseInt(queryShipment);
  const i = batch.shipments.findIndex(s => s.id === n);
  const focusedShipmentIndex = i < 0 ? 0 : i;
  return { focusedShipment: batch.shipments[focusedShipmentIndex], focusedShipmentIndex };
}

function boxesStateFromQueryParam(batch: FbaBatch, queryBoxes: string|null): BoxesState {
  const boxesUrlState = queryBoxes?.match(/^[0123]+/)?.[0].split('').map(c => Number(c)) ?? [];
  const boxesState: BoxesState = {};

  // iterate over boxes in the order they are represented in the query param
  const boxes = (batch?.boxes ?? []).toSorted((l, r) => l.id - r.id);
  for (const [i, box] of boxes.entries()) {
    boxesState[box.id] = {
      id: box.id,
      selected: (boxesUrlState[i] & 0b10) !== 0,
      open: (boxesUrlState[i] & 0b01) === 0, // open is default; bitflag is 0
      pgId: box.packingGroupId,
    };
  }
  return boxesState;
}
const boxesStateToQueryParam: (boxesState: BoxesState) => string = (boxesState) => {
  return Object.values(boxesState)
    .toSorted((l, r) => l.id - r.id)
    .map(bs => (Number(bs.selected) << 1) | Number(!bs.open))
    .join('');
};

const advanceStep = (
  batch: FbaBatch,
  setBarcodeError: (string) => void,
  onContinueClicked: () => void
) => {
  if (batch.boxContentsProvider === BoxContentsProvider.Barcode2D) {
    // first, we have to do a strange check. it is possible for the 2D barcode not to be able to be generated.
    // this is due to a data size limit in the PDF417 specification. 99.9% of cases this doesn't matter,
    // but it happened to a bookseller customer who was packing ~100 books into a single box. since that user
    // was only generating their box labels in step 4, they were put into an unrecoverable state where they had to re-make
    // the batch. here we will check before proceeding to if all the 2D barcodes can be succesfully generated.
    // if they can't we will let the user know they have to move stuff around in their boxes a bit before proceeding
    // (this amounts to them putting less products in a single box).
    for (const box of batch.boxes) {
      const boxContents: Barcode2DItem[] = [];
      box.packingItems.forEach(item => {
        const batchItem = getFbaBatchItemById(batch, item.batchItemId);
        const packingGroupItem = getPackingGroupItem(batch, box.packingGroupId, batchItem.sku);
        if (packingGroupItem) {
          boxContents.push({
            fnsku: packingGroupItem.fnsku,
            quantity: item.quantity,
            expiration: batchItem.expirationDate ? new Date(batchItem.expirationDate) : undefined,
          });
        }
      });

      // @NOTE the fake shipment id - we don't have a shipment id at this stage in v2.
      // doesn't matter, as long as it's approximately the right length. Here I doubled
      // the expected length, as a safety margin.
      const [error] = fbaBoxLabel2DBarcode("FBAXXXXXXXXXFBAXXXXXXXXX", boxContents);
      if (error === PDF417Error.DATA_SEGMENT_TOO_LARGE) {
        logRocketError(`Failed to generate 2D barcode for box contents: '${JSON.stringify(boxContents)}'`);
        setBarcodeError(`Box ${box.displayIndex} has too many different products inside of it, which would cause an error when trying to generate 2D barcodes for the box's label at a later stage. Please distribute some of the items within the box to other boxes.`)
        return;
      }
    }
  }
  onContinueClicked();
}

export default function PrepAndBoxemPage(props: FbaPageProps) {
  const {
    batch: _batch, // we want to refer to this via the ref to avoid stale closures
    continueButton,
    onContinueClicked: _onContinueClicked,
    onCancelClicked,
    onError,
    cloneShipment,
    shipmentDetails,
    draggingState,
  } = props;

  const {
    setCurrentBatch,
    boxTemplates,
    setBoxTemplatesDTO,
  } = useShipmentContext();

  const batchRef = useRef<FbaBatch>(_batch);
  useEffect(() => { batchRef.current = _batch }, [_batch])
  const setBatch = (batch: FbaBatch) => { batchRef.current = batch; setCurrentBatch(batch) };

  const spd = batchRef.current.shipmentType === TransportType.SPD;
  const ltl = batchRef.current.shipmentType === TransportType.LTL;

  const [labelsModalItems, setLabelsModalItems] = useState<LabelsModalItem[]>([]);

  const [barcodeError, setBarcodeError] = useState<string | null>(null);
  const onContinueClicked = () => advanceStep(
    batchRef.current,
    setBarcodeError,
    _onContinueClicked,
  );

  const [query, setQuery] = useSearchParams();

  const {focusedShipment, focusedShipmentIndex} = focusedShipmentFromQuery(batchRef.current, query.get('shipment'));
  const setFocusedShipment = (shipment: FbaShipment) => {
    setQuery(prev => {
      prev.set("shipment", shipment.id.toString());
      return prev;
    }, { replace: true });
  }

  const packingGroups = useMemo(() => focusedShipment
    ? batchRef.current.chosenPackingOption.packingGroups
        .filter(pg => pg.items.find(pi => focusedShipment.items.find(si => si.sku === pi.msku)))
    : batchRef.current.chosenPackingOption.packingGroups,
    [focusedShipment, batchRef.current?.chosenPackingOption?.packingGroups]
  );

  const selectedPackingGroup = selectedPackingGroupFromQuery(packingGroups, query.get('group'));
  const setSelectedPackingGroup = (group: string) => {
    setQuery(prev => {
      prev.set("group", group);
      return prev;
    }, { replace: true });
  }

  const boxesState = boxesStateFromQueryParam(batchRef.current, query.get("boxes"));
  const setBoxesState = (state: BoxesState) => {
    const boxes = boxesStateToQueryParam(state);
    setQuery(prev => {
      prev.set("boxes", boxes);
      return prev;
    }, { replace: true });
  };

  const [boxesLoading, setBoxesLoading] = useState(true);
  const [boxToEdit, _setBoxToEdit] = useState<FbaBox>(null);
  function setBoxToEdit(set?: FbaBox) {
    const doms = document.querySelectorAll(`[data-box-id-prep-n-boxem]`) as NodeListOf<HTMLElement>;
    for(const dom of doms) dom.style.backgroundColor = "unset";
    if(set && set.id) {
      const dom = document.querySelector(`[data-box-id-prep-n-boxem="${set?.id}"]`) as HTMLElement;
      if(dom) dom.style.backgroundColor = "rgba(255, 86, 35, 0.08)";
    }

    return _setBoxToEdit(set);
  }
  const [boxDetailsOpen, setBoxDetailsOpen] = useState(false);
  const [bulkAssignProps, setBulkAssignProps] = useState<AssignDialogProps>(null);
  const [assignAllSkusProps, setAssignAllSkusProps] = useState<AssignDialogProps>(null);
  const [showShipperModal, setShowShipperModal] = useState(false);
  const [showPalletModal, setShowPalletModal] = useState(false);
  const [shipperInformation, setShipperInformation] = useState<ShipperInformation>(null);


  const shipperModalClosed = async (value?: ShipperInformation, applyToAll?: boolean) => {
    const applyShipperInfo = async (shipment: FbaShipment) => {
      const patch: FbaShipmentPatch = {
        contactName: value.contactName,
        contactEmail: value.contactEmail,
        contactPhone: value.contactPhone,
        freightClass: value.freightClass,
        freightReadyDate: dayjs(value.freightReadyDate).format("YYYY-MM-DD"),
        declaredValue: value.declaredValue,
      };
      await patchShipmentV2(shipment.id, patch);
      Object.assign(shipment, patch);
    };

    if (value) {
      let requests: Promise<void>[];
      if (applyToAll) {
        requests = batchRef.current.shipments.map(applyShipperInfo);
      } else {
        requests = [applyShipperInfo(focusedShipment)];
      }
      try {
        await Promise.all(requests);
      } catch (e) {
        onError(e);
      } finally {
        setBatch({ ...batchRef.current, shipments: [...batchRef.current.shipments] });
        setShipperInformation(value);
        setLocalStorageItem(LocalStorageKeys.lastUsedShipperInformationContact, {
          contactName: value.contactName,
          contactEmail: value.contactEmail,
          contactPhone: value.contactPhone,
        });
      }
    }
    setShowShipperModal(false);
  };

  const palletModalClosed = async (pallets?: Pallet[]) => {
    if (pallets) {
      const patch: FbaShipmentPatch = {
        pallets: pallets.map(p => ({
          weight: p.weight.toString(),
          height: p.height.toString(),
          isStacked: p.isStackable,
          dimensionUnit: "IN",
          weightUnit: "POUNDS",
        })),
      };
      await patchShipmentV2(focusedShipment.id, patch);
      Object.assign(focusedShipment, patch);
      setBatch({ ...batchRef.current, shipments: [...batchRef.current.shipments] });
    }
    setShowPalletModal(false);
  };

  const createBoxes = async (createBoxCount: number, select?: boolean, templateId?: string) => {
    const defaults = getDefaultDimensionsAndWeight(boxTemplates, setBoxTemplatesDTO);
    const ltlData = focusedShipment ? { shipmentId: focusedShipment.id } : {};

    let length = defaults.length;
    let width = defaults.width;
    let height = defaults.height;
    let weight = defaults.weight;
    const template: null|BoxTemplate = boxTemplates.templates.find(template => template.id === templateId);
    if(template) {
      length = template.length;
      width = template.width;
      height = template.height;
      weight = template.weight;
    }

    const boxDtos = Array.from({length: createBoxCount}).map(() => ({
      length, width, height, weight, items: [], ...ltlData
    }));
    const pgId = selectedPackingGroup.inboundPackingGroupId;
    try {
      const boxes = await handleCreateBox(batchRef.current, setBatch, boxDtos, pgId, focusedShipment?.id);
      if (select) {
        for (const box of boxes) {
          boxesState[box.id] = {
            id: box.id,
            selected: true,
            open: true,
            pgId: box.packingGroupId,
          }
        }
        setBoxesState(boxesState);
      }
    } catch (e) {
      onError(e);
    }
  };
  const toggleSelectBox = (bs: BoxState) => setBoxesState({ ...boxesState, [bs.id]: { ...bs, selected: !bs.selected } });
  const toggleBoxOpen = (bs: BoxState) => setBoxesState({ ...boxesState, [bs.id]: { ...bs, open: !bs.open } });
  const selectAllBoxesForPackingGroup = (pgId: string, allChecked: boolean) => {
    const newBoxesState = mapValues(boxesState, bs => ({ ...bs, selected: bs.pgId === pgId ? !allChecked : bs.selected }));
    setBoxesState(newBoxesState);
  };

  const deleteBoxById = async (boxId: number) => {
    const OGboxes = batchRef.current.boxes;
    try {
      setBatch({ ...batchRef.current, boxes: OGboxes.filter(b => b.id !== boxId) });
      await deleteBoxV2(boxId);
    } catch (e) {
      onError(e);
      setBatch({ ...batchRef.current, boxes: OGboxes });
    }
  };
  const deleteBox = async (bs: BoxState) => {
    await deleteBoxById(bs.id);
  };

  const updateBoxes = (updates: BoxUpdate[]) => {
    const boxes = [...batchRef.current.boxes];
    for (const { boxId, newItem, item, quantity, removedItem, dimensionsAndWeight, packingGroupId } of updates) {
      const boxIndex = boxes.findIndex(b => b.id === boxId);
      const box = boxes[boxIndex] = { packingItems: [], ...boxes[boxIndex], ...dimensionsAndWeight };
      if (packingGroupId) box.packingGroupId = packingGroupId;
      box.packingItems = [...box.packingItems];
      const foundItem = item && box.packingItems.find(e => e.batchItemId === item.id || e.sku === item.sku)
      if (newItem) {
        if (foundItem) {
          foundItem.quantity += quantity; // if an already-present item is added as "new," we increment the quantity
        } else {
          box.packingItems.push({ batchItemId: item.id, quantity, sku: item.sku });
        }
      } else if (removedItem || quantity === 0) {
        box.packingItems = box.packingItems.filter(e => !(e.batchItemId === item.id || e.sku === item.sku));
      } else if(item) {
        foundItem.quantity = quantity;
      }
    }
    setBatch({ ...batchRef.current, boxes });
  };

  useEffect(() => {
    (async () => {
      // On batch load, we need to ensure several constraints are satisfied:

      // 1. Ensure all existing boxes have dimensions and weight. If missing, assign default dimensions
      //    and weight.
      // 2. Ensure that each box belongs to a packing group. If not, add box to the first packing group.
      const requests: Promise<BoxUpdate | { error: any }>[] = [];
      const defaults = getDefaultDimensionsAndWeight(boxTemplates, setBoxTemplatesDTO);
      const firstPgId = packingGroups[0].inboundPackingGroupId;
      let addedToFirstPg = false;
      for (const box of batchRef.current.boxes) {
        if (!(box.height && box.width && box.length && box.weight && box.packingGroupId)) {
          const dimensionsAndWeight = (box.height && box.width && box.length && box.weight) ? null : {
            weight: box.weight || defaults.weight,
            height: box.height || defaults.height,
            length: box.length || defaults.length,
            width: box.width || defaults.width,
          };
          if (!box.packingGroupId) {
            addedToFirstPg = true;
          }
          const packingGroupId = box.packingGroupId ? null : { packingGroupId: firstPgId }
          requests.push(
            updateBoxV2(box.id, {
              ...(box as any),
              ...dimensionsAndWeight,
              ...packingGroupId,
            })
            .then(() => ({ dimensionsAndWeight, ...packingGroupId, boxId: box.id }))
            .catch(e => ({ error: e }))
          );
        }
      }

      // 3. Ensure at least one box exists in the initially displayed packing group (group 1). Create if necessary.
      const firstPg = packingGroups[0];
      if (!addedToFirstPg) {
        if (!batchRef.current.boxes.find(box => box.packingGroupId === firstPg.inboundPackingGroupId)) {
          const createBoxes = [{
            packingGroupId: firstPg.inboundPackingGroupId,
            items: [],
            ...defaults,
          }];
          requests.push(
            handleCreateBox(batchRef.current, setBatch, createBoxes as any, null, focusedShipment?.id)
              .then(() => ({ noop: true as true }))
              .catch(e => ({ error: e }))
          );
        }
      }

      const results = await Promise.all(requests);
      const errors = results.filter((result: any) => !!result.error)
        .reduce((acc, result: any) => { acc.push(...(Array.isArray(result.error) ? result.error : [result.error])); return acc }, [])
      
      if (errors.length > 0) onError(errors);

      updateBoxes(results.filter(result => !(result as any).error) as BoxUpdate[]);

      // 4. Ensure that packing groups with a single box start out with the box already selected.
      const newBoxesState = {};
      for (const pg of batchRef.current.chosenPackingOption.packingGroups) {
        const pgBoxes = batchRef.current.boxes.filter(box => box.packingGroupId === pg.inboundPackingGroupId);
        if (pgBoxes.length === 1) {
          newBoxesState[pgBoxes[0].id] = {
            id: pgBoxes[0].id,
            selected: true,
            open: true,
            pgId: pg.inboundPackingGroupId,
          };
        }
      }
      setBoxesState({ ...boxesState, ...newBoxesState });
      setBoxesLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (focusedShipment) {
      const lastUsed: ShipperInformation = getLocalStorageItem(LocalStorageKeys.lastUsedShipperInformationContact);
      const shipperInformation: ShipperInformation = {
        contactName: focusedShipment.contactName || lastUsed?.contactName,
        contactEmail: focusedShipment.contactEmail || lastUsed?.contactEmail,
        contactPhone: focusedShipment.contactPhone || lastUsed?.contactPhone,
        freightClass: FreightClasses[focusedShipment.freightClass]?.value ?? null,
        declaredValue: focusedShipment.declaredValue,
        freightReadyDate: focusedShipment.freightReadyDate
          ? dayjs(focusedShipment.freightReadyDate, "YYYY-MM-DD").toDate()
          : dayjs().add(1, "day").toDate(),
      };

      setShipperInformation(shipperInformation);
    }
  }, [focusedShipment?.id]);

  const iconCaution = <IconCautionCircle fill="var(--sentiment-text-notice" size={17} />;
  const iconOk = <IconCheckCircle fill="var(--sentiment-text-positive" size={17} />;

  const itemsAssignedOk = useMemo(() => batchRef.current.shipments.map(s => null == s.items.find(item => getItemAssignedQuantity(batchRef.current, null, s.id, item.sku) < item.quantity)), [batchRef.current?.shipments, batchRef.current?.itemAssignedQuantities]);
  const palletsOk = useMemo(() => batchRef.current.shipments.map(s => s.pallets.length > 0), [batchRef.current?.shipments]);
  const shipperOk = useMemo(() => batchRef.current.shipments.map(s => !!(s.contactName && s.contactPhone)), [batchRef.current?.shipments]);
  const shipmentOk = useMemo(() => batchRef.current.shipments.map((s, i) => palletsOk[i] && shipperOk[i] && itemsAssignedOk[i]), [palletsOk, shipperOk, itemsAssignedOk]);
  const okShipmentCount = useMemo(() => shipmentOk.reduce((acc, ok) => ok ? acc + 1 : acc, 0), [shipmentOk]);

  return (
    <PageContent
      shrink={true}
      header={
        <PageHeader
          step={spd ? 1 : 2}
          subSteps={ltl ? [null, null, {cur: okShipmentCount, total: batchRef.current.shipments.length}] : null}
          shipmentType={batchRef.current.shipmentType}
          continueButton={continueButton}
          onContinueClicked={onContinueClicked}
          onCancelClicked={onCancelClicked}
        />
      }
    >
      <div className={styles.pageContent}>
        {spd && <InfoBanner
          message={
            <>
              If you’re shipping 5 or more identical boxes (same dimensions, weight, and unit count),
              your shipment may qualify for <span className="strong">“optimized placement.”</span>
            </>
          }
          storageKey={"optimizedPlacementBannerKey"}
        />}
        {ltl && <InfoBanner
          message={
            <>
              <span className="strong">Shipment {focusedShipmentIndex + 1}/{batchRef.current.shipments.length}.
              </span> The shipments have been created but have not yet been shipped. Make sure to enter
              your <span className="strong">“Pallet”</span> and <span className="strong">“Shipper”
              </span> information. This step is required for all shipments before proceeding.
            </>
          }
          storageKey={"palletAndShipperBannerKey"}
        />}
        <div className={styles.buttonRow}>
          <Button type="secondary" onClick={shipmentDetails}>
            <IconShipment />Shipment Details
          </Button>
          <Button type="secondary" onClick={() => {
            const items: LabelsModalItem[] = [];
            for (const packingItem of selectedPackingGroup.items) {
              const batchItem = batchRef.current.items.find(item => item.sku === packingItem.msku);
              items.push({
                batchItem,
                totalQuantity: packingItem.quantity,
                assignedQuantity: getItemAssignedQuantity(batchRef.current, selectedPackingGroup.inboundPackingGroupId, null, batchItem.sku),
                fnsku: packingItem.fnsku,
              });
            }
            setLabelsModalItems(items);
          }}>
            <IconPrintGoogle />Print
          </Button>
          <Button type="secondary" onClick={() => setBoxDetailsOpen(true)}>
            <IconBoxIso />Box details
          </Button>
          <Button type="secondary" onClick={() => cloneShipment(focusedShipment?.id)}>
            <IconCopyClipboard />Clone Shipment
          </Button>
        </div>
        {ltl && <div className={styles.shipmentRow}>
          Select your shipment:&nbsp;&nbsp;
          <Select
            style={{flex: 1}}
            fullWidth={true}
            highlightSelectedClass={(shipment, i) => shipmentOk[i] ? styles.selectedShipmentOption : undefined}
            value={focusedShipment}
            onChange={setFocusedShipment}
            options={batchRef.current.shipments}
            optionRender={(shipment, i) => (
              <span className={`strong ${styles.shipmentOption}`}>
                {shipmentOk[i] ? iconOk : iconCaution}
                <span>{shipment.shipmentName} {i + 1}/{batchRef.current.shipments.length}</span>
              </span>
            )}
          />
          <div style={{flex: 1, gap: "8px", justifyContent: "flex-end", display: "flex"}}>
            <Tooltip title={palletsOk[focusedShipmentIndex] ? "" : "This shipment is missing pallet information"}>
              <Button type="secondary" onClick={() => setShowPalletModal(true)}>
                <IconBoxIso />Pallet information{palletsOk[focusedShipmentIndex] ? iconOk : iconCaution}
              </Button>
            </Tooltip>
            <Tooltip title={shipperOk[focusedShipmentIndex] ? "" : "This shipment is missing shipper information"}>
              <Button type="secondary" onClick={() => setShowShipperModal(true)}>
                <IconShipment />Shipper information{shipperOk[focusedShipmentIndex] ? iconOk : iconCaution}
              </Button>
            </Tooltip>
          </div>
        </div>}
        <div className={styles.packingGroups}>
          {packingGroups.length === 1 ? (
            <PackingGroupView
              shipment={focusedShipment}
              boxesState={filterValues(boxesState, bs => bs.pgId === packingGroups[0].inboundPackingGroupId)}
              boxesLoading={boxesLoading}
              packingGroup={packingGroups[0]}
              createBoxes={createBoxes}
              toggleSelectBox={toggleSelectBox}
              toggleBoxOpen={toggleBoxOpen}
              selectAllBoxes={selectAllBoxesForPackingGroup}
              deleteBox={deleteBox}
              editBox={box => setBoxToEdit(box)}
              showBulkAssignModal={props => setBulkAssignProps({...props, open: true})}
              showAssignAllSkusModal={props => setAssignAllSkusProps({...props, open: true})}
              onBoxesUpdated={updateBoxes}
              onError={onError}
              draggingState={draggingState}
            />
          ) : (
            <Tabs
              shrink={true}
              activeTab={selectedPackingGroup.inboundPackingGroupId}
              setActiveTab={setSelectedPackingGroup}
              tabs={packingGroups.map((pg, index) => ({
                key: pg.inboundPackingGroupId,
                label: `Pack Group ${index + 1}`,
                children: (
                  <PackingGroupView
                    shipment={focusedShipment}
                    boxesState={filterValues(boxesState, bs => bs.pgId === pg.inboundPackingGroupId)}
                    boxesLoading={boxesLoading}
                    packingGroup={pg}
                    index={index}
                    createBoxes={createBoxes}
                    toggleSelectBox={toggleSelectBox}
                    toggleBoxOpen={toggleBoxOpen}
                    selectAllBoxes={selectAllBoxesForPackingGroup}
                    deleteBox={deleteBox}
                    editBox={box => setBoxToEdit(box)}
                    showBulkAssignModal={props => setBulkAssignProps({...props, open: true})}
                    showAssignAllSkusModal={props => setAssignAllSkusProps({...props, open: true})}
                    onBoxesUpdated={updateBoxes}
                    onError={onError}
                    draggingState={draggingState}
                  />
                ),
              }))}
            />
          )}
        </div>
      </div>
      <FbaUpdateBoxDimensionsModal
        box={boxToEdit}
        onClose={() => setBoxToEdit(null)}
      />
      <BoxDetailsModal
        createBoxes={createBoxes}
        boxes={batchRef.current.boxes}
        open={boxDetailsOpen}
        deleteBoxById={deleteBoxById}
        onClose={() => setBoxDetailsOpen(false)}
      />
      <ConfirmModal
        title={"Box Contents Error"}
        body={<p style={{margin:"20px",padding:"20px"}}>{barcodeError}</p>}
        open={!!barcodeError}
        onClose={() => setBarcodeError(null)}
      />
      <BulkAssignModal
        {...bulkAssignProps}
        shipmentId={focusedShipment?.id}
        onClose={() => setBulkAssignProps({...bulkAssignProps, open: false})}
        onError={onError}
      />
      <AssignAllSkusModal
        {...assignAllSkusProps}
        shipmentId={focusedShipment?.id}
        createNewBox={() => (
          handleCreateBox(
            batchRef.current,
            setBatch,
            [{ items: [], ...getDefaultDimensionsAndWeight(boxTemplates, setBoxTemplatesDTO) }],
            selectedPackingGroup.inboundPackingGroupId,
            focusedShipment?.id,
          ).then(([box]) => box)
        )}
        onUpdates={updateBoxes}
        selectedBoxes={batchRef.current.boxes.filter(box => boxesState[box.id].selected)}
        onClose={() => setAssignAllSkusProps({...assignAllSkusProps, open: false})}
        onError={onError}
      />
      <LabelsModal
        open={labelsModalItems?.length > 0}
        onClose={() => setLabelsModalItems([])}
        items={labelsModalItems}
      />
      <ShipperInformationModal
        shipmentsCount={batchRef.current.shipments.length}
        open={showShipperModal}
        onClose={shipperModalClosed}
        value={shipperInformation}
        shipment={focusedShipment}
      />
      <PalletInformationModal
        open={showPalletModal}
        onClose={palletModalClosed}
        value={focusedShipment?.pallets.map(p => ({
          height: Number(p.height),
          weight: Number(p.weight),
          isStackable: p.isStacked,
        }))}
      />
    </PageContent>
  );
}
